import { useDebounceFn } from 'ahooks';
import { useMemo, useState } from 'react';
import type { SortBy } from 'tea-component/lib/table/addons';
import { useIntl } from 'umi';
import { useTabTemplate } from '../../Home/hooks/useTabTemplate';
import {
  DEFAULT_FILE_LIBRARY_SORT,
  FILE_LIBRARY_PAGE_TABS,
  INIT_FILE_LIBRARY_QUERY,
} from '../constants';
import type {
  TFileLibraryQuery,
  TFileLibraryQueryInput,
  TFileLibraryUploadType,
} from '../types';
import {
  getActiveFilterCount,
  getSortFromTableSort,
  getSortInfoFromQuery,
} from '../utils/query';

/**
 * 文件库查询态：页面级单例一份，不做任何跨页签记忆。
 * 切换页签时 keyword / 筛选 / 排序整体重置为默认；uploadType 随当前页签派生。
 */
export const useFileLibraryQuery = () => {
  const { formatMessage } = useIntl();
  /** 用户可调查询项（keyword / 筛选 / 排序） */
  const [query, setQuery] = useState<TFileLibraryQueryInput>(
    INIT_FILE_LIBRARY_QUERY,
  );
  /** 搜索框受控文本，与 query.keyword 差一个 debounce 周期 */
  const [searchDraft, setSearchDraft] = useState('');

  const pageTabs = useMemo(
    () =>
      FILE_LIBRARY_PAGE_TABS.map(tab => ({
        id: tab.id,
        label: formatMessage({ id: tab.labelId }),
      })),
    [formatMessage],
  );

  const { activeId, onTabChange } = useTabTemplate({
    tabs: pageTabs,
    defaultActiveId: 'all',
  });

  const activeUploadType = (activeId || 'all') as TFileLibraryUploadType;

  /** 对外完整查询态：uploadType 始终随当前页签 */
  const listQuery = useMemo<TFileLibraryQuery>(
    () => ({ ...query, uploadType: activeUploadType }),
    [query, activeUploadType],
  );

  const patchQuery = (patch: Partial<TFileLibraryQueryInput>): void => {
    setQuery(prev => ({ ...prev, ...patch }));
  };

  const commitSearchKeyword = (text: string): void => {
    patchQuery({ keyword: text.trim().toLowerCase() });
  };

  const { run: debouncedCommitSearch, cancel: cancelDebouncedSearch } =
    useDebounceFn(commitSearchKeyword, { wait: 500 });

  const handleSearchChange = (text?: string): void => {
    const value = text ?? '';
    setSearchDraft(value);
    if (!value.trim()) {
      cancelDebouncedSearch();
      patchQuery({ keyword: '' });
      return;
    }
    debouncedCommitSearch(value);
  };

  /**
   * 切换页签：点击当前已激活页签不重置；切换到其它页签时
   * 取消未生效搜索并整体重置为默认，避免误伤正在使用的筛选条件。
   */
  const handleTabChange = (nextId: string): void => {
    if (nextId === activeId) {
      return;
    }
    cancelDebouncedSearch();
    setQuery(INIT_FILE_LIBRARY_QUERY);
    setSearchDraft('');
    onTabChange(nextId);
  };

  const sortInfo = useMemo(
    () => getSortInfoFromQuery(listQuery.sort),
    [listQuery.sort.asc, listQuery.sort.key],
  );

  const handleSortChange = (value?: SortBy<string>): void => {
    // tea sortable（prefer: desc）在 asc 再点时会传空数组，不能 fallback 旧 sortInfo，否则 UI 卡住
    if (!value?.by || !value?.order) {
      const currentKey = listQuery.sort.key;
      if (currentKey && listQuery.sort.asc) {
        patchQuery({ sort: { key: currentKey, asc: false } });
        return;
      }
      patchQuery({ sort: DEFAULT_FILE_LIBRARY_SORT });
      return;
    }
    patchQuery({ sort: getSortFromTableSort(value) });
  };

  const activeFilterCount = getActiveFilterCount(listQuery);

  return {
    pageTabs,
    activeId,
    onTabChange: handleTabChange,
    listQuery,
    patchQuery,
    searchDraft,
    handleSearchChange,
    sortInfo,
    handleSortChange,
    activeFilterCount,
  };
};
